import React, { useEffect, useState } from "react";
import {
  Card,
  Row,
  Col,
  Typography,
  Divider,
  Upload,
  Button,
  message,
} from "antd";
import {
  CheckCircleFilled,
  CreditCardOutlined,
  FileDoneOutlined,
} from "@ant-design/icons";
import {
  deleteUploadedDocument,
  getAllStarhealthDocumentsData,
  getPreAuthStepper,
  updateAllDocumentsData,
} from "../../apis/patientService";
import { Spin } from "antd";
import DocumentUploadPreview from "./DocumentUploadPreview";
import { useDispatch } from "react-redux";
import { AppDispatch } from "../../store/store";
import { uploadDocs } from "../../apis/caseService";
import MobileUploadedDocsModal from "./MobileUploadedDocsModal";
import { Popover } from "antd";

const { Text } = Typography;

export interface PatientCardProps {
  creditNumber: string;
  patientDetails: any;
  name: string;
  registrationNo: string;
  date: string;
  type: any;
  uhid?: string;
  primaryMobile?: string;
}

const PatientCard: React.FC<PatientCardProps> = ({
  creditNumber,
  patientDetails,
  name,
  registrationNo,
  date,
  type,
  uhid,
  primaryMobile,
}) => {
  const [expanded, setExpanded] = useState(false);
  const [loading, setLoading] = useState<boolean>(false);
  const [documentTypes, setDocumentTypes] = useState<any>([]);
  const documentAbbreviations: any = {
    "HOSPITAL BILL": "Hsp. Bill",
    "Geo-Tagged Photo of Patient": "Geo Tag",
    "INVESTIGATION REPORTS": "Reports",
    "Consultation Notes": "Con. Notes",
    OTHERS: "Others",
  };
  const [isUploading, setIsUploading] = useState<boolean>(false);
  const [viewUploadedDocs, setViewUploadedDocs] = useState<boolean>(false);
  const [docDetails, setDocDetails] = useState<any>(null);
  const [stagesWithId, setStagesWithId] = useState<any>({});

  const [selectedFile, setSelectedFile] = useState<any>({
    file: {},
    documentType: "",
    stage: "",
  });
  const [uploadFileModal, setUploadFileModal] = useState<boolean>(false);

  const handleDeleteList = async (documentUrl: string, documentUrls: any) => {
    const combinedUrls: any = [...documentUrls];
    const deleteDocument = combinedUrls?.find(
      (doc: any, index: any) => doc?.documentUrl === documentUrl,
    );
    const deleteForm = {
      documentUrl: deleteDocument?.documentUrl,
      status: false,
      id: deleteDocument?.id,
    };

    const newUpdatedDocumentList = combinedUrls?.filter(
      (doc: any, index: any) => doc?.documentUrl !== documentUrl,
    );

    if (deleteDocument?.documentUrl) {
      try {
        await deleteUploadedDocument(deleteForm);
      } catch (err) {
        console.error(err);
      }
      fetchDocuments(stagesWithId);
    }
  };

  const handleFileChange =
    (documentType: string, stage: string) => (e: any) => {
      if (Array.isArray(e)) {
        return e;
      }

      if (e?.fileList[0]) {
        setSelectedFile({ file: e?.fileList[0], documentType, stage });
        setUploadFileModal(true);
      }

      return e?.fileList;
    };

  const handleSaveData = async (documentUrls: any, stage: any) => {
    const payloadSchema: {
      casePreAuthAttachments: any[];
      caseEnhancementsAttachments: any[];
      caseDischargeAttachments: any[];
      caseClaimAttachments: any[];
      caseBillAuditAttachments: any[];
    } = {
      casePreAuthAttachments: [],
      caseEnhancementsAttachments: [],
      caseDischargeAttachments: [],
      caseClaimAttachments: [],
      caseBillAuditAttachments: [],
    };

    const stageKeyMap: Record<string, keyof typeof payloadSchema> = {
      PreAuth: "casePreAuthAttachments",
      Enhancement: "caseEnhancementsAttachments",
      Discharge: "caseDischargeAttachments",
      Claim: "caseClaimAttachments",
      "Bill Audit": "caseBillAuditAttachments",
    };

    const payloadKey = stageKeyMap[stage];

    if (payloadKey) {
      payloadSchema[payloadKey] = documentUrls;
    }

    const caseId = patientDetails?.caseId;

    const visitId = patientDetails?.visitId;
    const schemeMetaDataId = patientDetails?.schemeMetaDataId;

    if (caseId && visitId && schemeMetaDataId) {
      try {
        const AllDocumentList = await updateAllDocumentsData(
          caseId,
          visitId,
          payloadSchema,
        );

        fetchDocuments(stagesWithId);
      } catch (error) {}
    }
  };

  useEffect(() => {
    if (viewUploadedDocs && docDetails) {
      setTimeout(() => {
        const updatedDocument = documentTypes?.find(
          (doc: any) =>
            doc.labelId === docDetails.documentType &&
            doc.stage === docDetails.stage,
        );
        if (updatedDocument) {
          setDocDetails({
            ...docDetails,
            documentList: updatedDocument.documentList,
          });
        }
      }, 100);
    }
  }, [viewUploadedDocs, docDetails, documentTypes]);

  const handleUpload = async () => {
    const CollectPostData = new FormData();
    if (patientDetails?.healthCareId) {
      CollectPostData.append(
        "hospitalId",
        patientDetails?.healthCareId?.toString(),
      );
    }
    if (patientDetails?.patientId) {
      CollectPostData.append(
        "patientId",
        patientDetails?.patientId?.toString(),
      );
    }
    if (patientDetails?.caseId) {
      CollectPostData.append("caseId", patientDetails?.caseId?.toString());
    }
    if (patientDetails?.visitId) {
      CollectPostData.append("visitId", patientDetails?.visitId?.toString());
    }
    CollectPostData.append("documentType", selectedFile.documentType);

    if (selectedFile?.file) {
      CollectPostData.append("file", selectedFile?.file?.originFileObj);
    }

    try {
      setIsUploading(true);
      const documentRes = await uploadDocs(CollectPostData);

      setUploadFileModal(false);
      message.success(
        `${documentRes?.[0]?.documentLabelType} uploaded successfully`,
      );
      await handleSaveData(documentRes, selectedFile?.stage);
      setSelectedFile({
        file: {},
        documentType: "",
      });
    } catch (error) {
      //   console.error("Failed to upload document:", error);
    } finally {
      setIsUploading(false);
    }
  };

  const ActionButton = ({
    label,
    icon,
    documentType,
    stage,
    documentList,
    isMultiple,
  }: {
    label: string;
    icon: React.ReactNode;
    documentType: any;
    stage: string;
    documentList: any;
    isMultiple: boolean;
  }) =>
    documentList ? (
      <Button
        style={{ padding: "2px" }}
        icon={icon}
        onClick={() => {
          setViewUploadedDocs(true);
          setDocDetails({
            label,
            documentList,
            stage,
            documentType,
            isMultiple,
          });
        }}
      >
        <span style={{ fontSize: 7, marginLeft: "-3px" }}>
          {documentAbbreviations?.[label]}
          <CheckCircleFilled
            style={{
              color: "#22c55e",
              fontSize: 7,
              marginLeft: 3,
            }}
          />
        </span>
      </Button>
    ) : (
      <Upload
        beforeUpload={() => false}
        onChange={handleFileChange(documentType, stage)}
        maxCount={1}
        showUploadList={false}
      >
        <Button icon={icon} style={{ padding: "2px" }}>
          <span style={{ fontSize: 7, marginLeft: "-3px" }}>
            {documentAbbreviations?.[label]}
          </span>
        </Button>
      </Upload>
    );

  const fetchDocuments = async (updatedStages: any) => {
    if (
      patientDetails?.schemeMetaDataId &&
      patientDetails?.caseId &&
      patientDetails?.visitId
    ) {
      try {
        setLoading(true);
        const AllDocumentList = await getAllStarhealthDocumentsData(
          patientDetails?.caseId,
          patientDetails?.visitId,
          patientDetails?.schemeMetaDataId,
        );
        const requiredLabels = [
          "HOSPITAL BILL"?.toUpperCase(),
          "Geo-Tagged Photo of Patient"?.toUpperCase(),
          "INVESTIGATION REPORTS"?.toUpperCase(),
          "Consultation Notes"?.toUpperCase(),
          "OTHERS"?.toUpperCase(),
        ];

        const seen = new Set();

        const result = Object.entries(AllDocumentList).flatMap(
          ([stageName, documents]: any) =>
            documents
              ?.filter((doc: any) => {
                const label = doc.label?.toUpperCase();
                if (!requiredLabels.includes(label)) return false;
                if (
                  label === "OTHERS" &&
                  stageName?.toUpperCase() !== "CLAIM"
                ) {
                  return false;
                }

                return true;
              })
              ?.filter((doc: any) => {
                if (seen.has(doc.id)) return false;
                seen.add(doc.id);
                return true;
              })
              ?.map((doc: any) => {
                const formatted = {
                  label: doc.label,
                  labelId: doc.labelId,
                  multiple: doc.multiple,
                  id: doc.id,
                  documentList: doc.documentList,
                  stage: stageName,
                  stageId: updatedStages?.[stageName?.toUpperCase()],
                };

                return formatted;
              }),
        );

        setDocumentTypes(result);
      } catch (err) {
      } finally {
        setLoading(false);
      }
    }
  };

  const fetchStages = async () => {
    try {
      setLoading(true);
      const res = await getPreAuthStepper(
        patientDetails?.caseId,
        patientDetails?.visitId,
      );
      const updatedStages = res?.visitStages?.reduce((acc: any, item: any) => {
        acc[item?.visitStage?.toUpperCase()] = item?.visitStageId;
        return acc;
      }, {});

      fetchDocuments(updatedStages);
      setStagesWithId(updatedStages);
    } catch (err) {
      setStagesWithId({});
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (expanded) {
      fetchStages();
    }
  }, [patientDetails, expanded]);

  return (
    <>
      <Card
        bordered
        onClick={() => setExpanded(!expanded)}
        style={{
          borderRadius: 8,
          marginBottom: 12,
          fontSize: 12,
          border: "1px solid #AADFFF",
          // cursor: "pointer",
        }}
        bodyStyle={{ padding: "6px 8px" }}
      >
        {/* Top Info */}
        <Row gutter={[24, 24]}>
          <Col span={10}>
            <CreditCardOutlined style={{ fontSize: 8, color: "#717182" }} />
            <Text
              type="secondary"
              style={{ fontSize: 8, marginLeft: 3, color: "#00395E80" }}
            >
              {creditNumber}
            </Text>
          </Col>

          <Col span={8}>
            <Text type="secondary" style={{ fontSize: 8, color: "#00395E80" }}>
              {patientDetails?.schemeName?.toUpperCase() === "ECHS"
                ? "Claim Id"
                : "Registration No."}
            </Text>
          </Col>

          <Col span={6} style={{ textAlign: "right", color: "#00060999" }}>
            <Text type="secondary" style={{ fontSize: 8 }}>
              {date}
            </Text>
          </Col>
        </Row>

        <Row gutter={[24, 24]}>
          <Col span={10}>
            <Text strong style={{ fontSize: 9, color: "#00395E" }}>
              {name}
            </Text>
          </Col>

          <Col span={8}>
            <Text strong style={{ fontSize: 9, color: "#00395E" }}>
              {registrationNo || "-"}
            </Text>
          </Col>

          <Col span={6} style={{ textAlign: "right" }}>
            <Popover content={type} trigger={["click"]}>
              <Text strong style={{ fontSize: 9, color: "#00395E" }}>
                {type?.toUpperCase() === "EMERGENCY" ? "EMG" : type}
              </Text>
            </Popover>
          </Col>
        </Row>

        {/* Expanded Section */}
        {expanded && (
          <>
            {/* UHID */}
            <Row gutter={[24, 24]}>
              <Col span={10}>
                <Text
                  type="secondary"
                  style={{ fontSize: 8, color: "#00395E80" }}
                >
                  UHID
                </Text>
              </Col>
              <Col span={8}>
                <Text
                  type="secondary"
                  style={{ fontSize: 8, color: "#00395E80" }}
                >
                  Primary Mobile
                </Text>
              </Col>
            </Row>

            <Row gutter={[24, 24]}>
              <Col span={10}>
                <Text strong style={{ fontSize: 9, color: "#00395E" }}>
                  {uhid || "-"}
                </Text>
              </Col>
              <Col span={8}>
                <Text strong style={{ fontSize: 9, color: "#00395E" }}>
                  {primaryMobile || "-"}
                </Text>
              </Col>
            </Row>

            <Divider style={{ margin: "8px 0" }} />

            {loading ? (
              <div
                style={{
                  display: "flex",
                  justifyContent: "center",
                  alignItems: "center",
                  width: "100%",
                }}
              >
                <Spin />
              </div>
            ) : (
              <Row gutter={[2, 2]}>
                {documentTypes?.map((document: any) => (
                  <Col
                    span={6}
                    key={document?.id}
                    onClick={(e) => e.stopPropagation()}
                  >
                    <ActionButton
                      label={document?.label}
                      icon={<FileDoneOutlined />}
                      stage={document?.stage}
                      documentType={document?.labelId}
                      documentList={document?.documentList}
                      isMultiple={document?.multiple}
                    />
                  </Col>
                ))}
              </Row>
            )}
          </>
        )}
      </Card>
      <DocumentUploadPreview
        uploadFileModal={uploadFileModal}
        setUploadFileModal={setUploadFileModal}
        selectedFile={selectedFile}
        isLoading={isUploading}
        handleUpload={handleUpload}
      />

      <MobileUploadedDocsModal
        visible={viewUploadedDocs}
        onClose={() => setViewUploadedDocs(false)}
        documents={docDetails?.documentList}
        title={docDetails?.label}
        onDelete={handleDeleteList}
        documentType={docDetails?.documentType}
        stage={docDetails?.stage}
        isMultiple={docDetails?.isMultiple}
        handleFileChange={handleFileChange}
        key={docDetails?.documentList?.length}
      />
    </>
  );
};

export default PatientCard;
